import React, { useState, useEffect, useRef } from 'react';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import Modal from '../components/Modal';

const Profile = () => {
  const { user, logout, updateProfile } = useAuth();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showNameModal, setShowNameModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  // Form states
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Load saved profile from localStorage
  const savedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  
  // Address states
  const [billingAddress, setBillingAddress] = useState(savedProfile.billingAddress || '');
  const [billingAddress2, setBillingAddress2] = useState(savedProfile.billingAddress2 || '');
  const [billingCity, setBillingCity] = useState(savedProfile.billingCity || '');
  const [billingState, setBillingState] = useState(savedProfile.billingState || '');
  const [billingZip, setBillingZip] = useState(savedProfile.billingZip || '');
  const [billingCountry, setBillingCountry] = useState(savedProfile.billingCountry || 'ZA');
  
  const [shippingAddress, setShippingAddress] = useState(savedProfile.shippingAddress || '');
  const [shippingAddress2, setShippingAddress2] = useState(savedProfile.shippingAddress2 || '');
  const [shippingCity, setShippingCity] = useState(savedProfile.shippingCity || '');
  const [shippingState, setShippingState] = useState(savedProfile.shippingState || '');
  const [shippingZip, setShippingZip] = useState(savedProfile.shippingZip || '');
  const [shippingCountry, setShippingCountry] = useState(savedProfile.shippingCountry || 'ZA');
  const [sameAsBilling, setSameAsBilling] = useState(savedProfile.sameAsBilling ?? true);

  // Address autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionType, setActiveSuggestionType] = useState(''); // 'billing' or 'shipping'
  const debounceTimer = useRef(null);

  // Load addresses from database on mount
  useEffect(() => {
    const loadAddressFromDatabase = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('billing_address, billing_address2, billing_city, billing_state, billing_zip, billing_country, shipping_address, shipping_address2, shipping_city, shipping_state, shipping_zip, shipping_country, shipping_same_as_billing')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error loading addresses:', error);
          return;
        }

        if (data) {
          // Update state with database values
          if (data.billing_address) setBillingAddress(data.billing_address);
          if (data.billing_address2) setBillingAddress2(data.billing_address2);
          if (data.billing_city) setBillingCity(data.billing_city);
          if (data.billing_state) setBillingState(data.billing_state);
          if (data.billing_zip) setBillingZip(data.billing_zip);
          if (data.billing_country) setBillingCountry(data.billing_country);
          
          if (data.shipping_address) setShippingAddress(data.shipping_address);
          if (data.shipping_address2) setShippingAddress2(data.shipping_address2);
          if (data.shipping_city) setShippingCity(data.shipping_city);
          if (data.shipping_state) setShippingState(data.shipping_state);
          if (data.shipping_zip) setShippingZip(data.shipping_zip);
          if (data.shipping_country) setShippingCountry(data.shipping_country);
          if (data.shipping_same_as_billing !== undefined) setSameAsBilling(data.shipping_same_as_billing);

          // Also update localStorage for quick access
          const profileData = {
            ...savedProfile,
            billingAddress: data.billing_address || '',
            billingAddress2: data.billing_address2 || '',
            billingCity: data.billing_city || '',
            billingState: data.billing_state || '',
            billingZip: data.billing_zip || '',
            billingCountry: data.billing_country || 'ZA',
            shippingAddress: data.shipping_address || '',
            shippingAddress2: data.shipping_address2 || '',
            shippingCity: data.shipping_city || '',
            shippingState: data.shipping_state || '',
            shippingZip: data.shipping_zip || '',
            shippingCountry: data.shipping_country || 'ZA',
            sameAsBilling: data.shipping_same_as_billing ?? true
          };
          localStorage.setItem('userProfile', JSON.stringify(profileData));
        }
      } catch (err) {
        console.error('Error loading address data:', err);
      }
    };

    loadAddressFromDatabase();
  }, [user?.id]);

  // Geoapify address autocomplete
  const searchAddress = async (query, type) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey) {
      console.error('Geoapify API key not found');
      return;
    }

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&apiKey=${apiKey}&limit=5`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setAddressSuggestions(data.features);
        setShowSuggestions(true);
        setActiveSuggestionType(type);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleAddressInputChange = (value, type) => {
    if (type === 'billing') {
      setBillingAddress(value);
    } else {
      setShippingAddress(value);
    }

    // Debounce the API call
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchAddress(value, type);
    }, 300);
  };

  const selectAddress = (suggestion) => {
    const props = suggestion.properties;
    const type = activeSuggestionType;

    // Extract address components
    const street = props.address_line1 || props.street || '';
    const city = props.city || '';
    const state = props.state || props.county || '';
    const zip = props.postcode || '';
    const country = props.country_code?.toUpperCase() || '';

    if (type === 'billing') {
      setBillingAddress(street);
      setBillingCity(city);
      setBillingState(state);
      setBillingZip(zip);
      setBillingCountry(country);
    } else {
      setShippingAddress(street);
      setShippingCity(city);
      setShippingState(state);
      setShippingZip(zip);
      setShippingCountry(country);
    }

    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showErrorMessage = (message) => {
    setError(message);
    setTimeout(() => setError(''), 3000);
  };

  const handleUpload = async (e) => {
    if (e.target && e.target.files) {
      const file = e.target.files[0];
      if (file) {
        setLoading(true);
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (!authUser) throw new Error('Not authenticated');

          // Delete old profile picture if exists
          if (user?.profilePicture && user.profilePicture.includes('supabase')) {
            const oldPath = user.profilePicture.split('/').pop();
            await supabase.storage
              .from('profile-pictures')
              .remove([`${authUser.id}/${oldPath}`]);
          }

          // Upload new profile picture
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}.${fileExt}`;
          const filePath = `${authUser.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('profile-pictures')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('profile-pictures')
            .getPublicUrl(filePath);

          // Update database
          const { error: updateError } = await supabase
            .from('users')
            .update({ profile_picture: publicUrl })
            .eq('id', authUser.id);

          if (updateError) throw updateError;

          await updateProfile({ profilePicture: publicUrl });
          showSuccessMessage('Profile picture updated successfully!');
        } catch (err) {
          console.error('Error uploading profile picture:', err);
          showErrorMessage('Failed to upload profile picture. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showErrorMessage('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .update({ full_name: name.trim() })
        .eq('id', authUser.id);

      if (error) throw error;

      await updateProfile({ name: name.trim() });
      setShowNameModal(false);
      showSuccessMessage('Name updated successfully!');
    } catch (err) {
      console.error('Error updating name:', err);
      showErrorMessage('Failed to update name. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      showErrorMessage('Phone number cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .update({ phone: phone.trim() })
        .eq('id', authUser.id);

      if (error) throw error;

      await updateProfile({ phone: phone.trim() });
      setShowPhoneModal(false);
      showSuccessMessage('Phone number updated successfully!');
    } catch (err) {
      console.error('Error updating phone:', err);
      showErrorMessage('Failed to update phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      showErrorMessage('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      showErrorMessage('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showErrorMessage('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSuccessMessage('Password updated successfully!');
    } catch (err) {
      console.error('Error updating password:', err);
      showErrorMessage(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      // Update database
      const { error: dbError } = await supabase
        .from('users')
        .update({
          billing_address: billingAddress,
          billing_address2: billingAddress2,
          billing_city: billingCity,
          billing_state: billingState,
          billing_zip: billingZip,
          billing_country: billingCountry,
          shipping_address: sameAsBilling ? billingAddress : shippingAddress,
          shipping_address2: sameAsBilling ? billingAddress2 : shippingAddress2,
          shipping_city: sameAsBilling ? billingCity : shippingCity,
          shipping_state: sameAsBilling ? billingState : shippingState,
          shipping_zip: sameAsBilling ? billingZip : shippingZip,
          shipping_country: sameAsBilling ? billingCountry : shippingCountry,
          shipping_same_as_billing: sameAsBilling
        })
        .eq('id', authUser.id);

      if (dbError) throw dbError;

      // Also save to localStorage for quick access
      const profileData = {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        email: user?.email,
        phone: user?.phone,
        billingAddress,
        billingAddress2,
        billingCity,
        billingState,
        billingZip,
        billingCountry,
        shippingAddress: sameAsBilling ? billingAddress : shippingAddress,
        shippingAddress2: sameAsBilling ? billingAddress2 : shippingAddress2,
        shippingCity: sameAsBilling ? billingCity : shippingCity,
        shippingState: sameAsBilling ? billingState : shippingState,
        shippingZip: sameAsBilling ? billingZip : shippingZip,
        shippingCountry: sameAsBilling ? billingCountry : shippingCountry,
        sameAsBilling
      };

      localStorage.setItem('userProfile', JSON.stringify(profileData));
      setShowAddressModal(false);
      showSuccessMessage('Address saved to database successfully!');
    } catch (err) {
      console.error('Error updating address:', err);
      showErrorMessage('Failed to save address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{
            position: 'relative',
            width: '150px',
            height: '150px',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '4px solid #CDB688',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: user?.profilePicture ? 'transparent' : 'linear-gradient(135deg, #f5f5f5, #e0e0e0)'
            }}>
              {user?.profilePicture ? (
                <img 
                  src={user.profilePicture} 
                  alt="Profile" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <i className="fas fa-user" style={{
                  fontSize: '4rem',
                  color: '#999'
                }}></i>
              )}
            </div>
            
            <label htmlFor="file-upload" style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '45px',
              height: '45px',
              background: loading ? '#999' : '#CDB688',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s ease',
              pointerEvents: loading ? 'none' : 'auto'
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {loading ? (
                <i className="fas fa-spinner fa-spin" style={{
                  color: 'white',
                  fontSize: '1.2rem'
                }}></i>
              ) : (
                <i className="fas fa-camera" style={{
                  color: 'white',
                  fontSize: '1.2rem'
                }}></i>
              )}
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleUpload}
              style={{display: 'none'}}
              disabled={loading}
            />
          </div>
          
          <h2 style={{
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.25rem'
          }}>Update Profile Picture</h2>
          <p style={{
            fontSize: '0.85rem',
            color: '#666',
            textAlign: 'center'
          }}>Click the camera icon to upload a new photo</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="member-info mb-xl">
          <p>Membership Number: {user?.email}</p>
          <p>Membership Level: {user?.membershipLevel}</p>
        </div>

        <div className="settings-section">
          <h3 className="section-title">Account Settings</h3>

          <div className="setting-item" onClick={() => {
            setName(user?.name || '');
            setShowNameModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Your name</div>
              <div className="setting-value">{user?.name || 'Not set'}</div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item">
            <div>
              <div className="setting-label">Your email</div>
              <div className="setting-value">{user?.email}</div>
            </div>
            <span style={{ opacity: 0.5, fontSize: '0.9rem' }}>Cannot be changed</span>
          </div>

          <div className="setting-item" onClick={() => {
            setPhone(user?.phone || '');
            setShowPhoneModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Your phone number</div>
              <div className="setting-value">{user?.phone || 'Not set'}</div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item" onClick={() => {
            const saved = JSON.parse(localStorage.getItem('userProfile') || '{}');
            setBillingAddress(saved.billingAddress || '');
            setBillingAddress2(saved.billingAddress2 || '');
            setBillingCity(saved.billingCity || '');
            setBillingState(saved.billingState || '');
            setBillingZip(saved.billingZip || '');
            setBillingCountry(saved.billingCountry || 'ZA');
            setShippingAddress(saved.shippingAddress || '');
            setShippingAddress2(saved.shippingAddress2 || '');
            setShippingCity(saved.shippingCity || '');
            setShippingState(saved.shippingState || '');
            setShippingZip(saved.shippingZip || '');
            setShippingCountry(saved.shippingCountry || 'ZA');
            setSameAsBilling(saved.sameAsBilling ?? true);
            setShowAddressModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Billing & Shipping Address</div>
              <div className="setting-value">
                {savedProfile.billingAddress ? `${savedProfile.billingAddress}, ${savedProfile.billingCity || ''}` : 'Not set'}
              </div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item" onClick={() => {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Password</div>
            </div>
            <span className="edit-link">Update</span>
          </div>

          <div className="setting-item" style={{ borderBottom: 'none' }}>
            <button className="btn btn-secondary w-full" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Name Edit Modal */}
      <Modal isOpen={showNameModal} onClose={() => setShowNameModal(false)} title="Edit Name">
        <form onSubmit={handleUpdateName}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Full Name
            </label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              disabled={loading}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowNameModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Phone Edit Modal */}
      <Modal isOpen={showPhoneModal} onClose={() => setShowPhoneModal(false)} title="Edit Phone Number">
        <form onSubmit={handleUpdatePhone}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Phone Number
            </label>
            <input
              type="tel"
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              disabled={loading}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowPhoneModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Password Update Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Update Password">
        <form onSubmit={handleUpdatePassword}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              New Password
            </label>
            <input
              type="password"
              className="input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading}
              required
              minLength={6}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
              required
              minLength={6}
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowPasswordModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Address Edit Modal */}
      <Modal isOpen={showAddressModal} onClose={() => setShowAddressModal(false)} title="Edit Address">
        <form onSubmit={handleUpdateAddress} style={{
          maxHeight: window.innerWidth < 600 ? '75vh' : '70vh', 
          overflowY: 'auto',
          padding: window.innerWidth < 400 ? '0.5rem' : '0'
        }}>
          {/* Billing Address Section */}
          <h3 style={{
            marginBottom: '1rem', 
            color: '#CDB688', 
            fontSize: window.innerWidth < 400 ? '1rem' : '1.1rem'
          }}>
            <i className="fas fa-file-invoice"></i> Billing Address
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500',
              fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
            }}>
              Address Line 1 * <span style={{color: '#999', fontSize: window.innerWidth > 600 ? '0.8rem' : '0.75rem', fontWeight: 'normal', display: window.innerWidth < 400 ? 'block' : 'inline', marginTop: window.innerWidth < 400 ? '0.25rem' : '0'}}>(Enter your full address)</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="input"
                value={billingAddress}
                onChange={(e) => handleAddressInputChange(e.target.value, 'billing')}
                onFocus={() => { if (billingAddress.length >= 3) searchAddress(billingAddress, 'billing'); }}
                placeholder="Start typing your address..."
                required
                autoComplete="off"
                style={{ fontSize: '16px' }}
              />
              {showSuggestions && activeSuggestionType === 'billing' && addressSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  marginTop: '4px'
                }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => selectAddress(suggestion)}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: index < addressSuggestions.length - 1 ? '1px solid #eee' : 'none',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {suggestion.properties.formatted}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500',
              fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
            }}>
              Address Line 2
            </label>
            <input
              type="text"
              className="input"
              value={billingAddress2}
              onChange={(e) => setBillingAddress2(e.target.value)}
              placeholder="Apartment, suite, etc."
              style={{ fontSize: '16px' }}
            />
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
            gap: '1rem', 
            marginBottom: '1rem' 
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                City *
              </label>
              <input
                type="text"
                className="input"
                value={billingCity}
                onChange={(e) => setBillingCity(e.target.value)}
                placeholder="City"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                State/Province *
              </label>
              <input
                type="text"
                className="input"
                value={billingState}
                onChange={(e) => setBillingState(e.target.value)}
                placeholder="State"
                required
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
            gap: '1rem', 
            marginBottom: '1rem' 
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                ZIP/Postal Code *
              </label>
              <input
                type="text"
                className="input"
                value={billingZip}
                onChange={(e) => setBillingZip(e.target.value)}
                placeholder="Postal code"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                Country *
              </label>
              <select
                className="input"
                value={billingCountry}
                onChange={(e) => setBillingCountry(e.target.value)}
                required
                style={{ fontSize: '16px' }}
              >
                <option value="ZA">South Africa</option>
                <option value="AE">United Arab Emirates</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="SA">Saudi Arabia</option>
                <option value="AU">Australia</option>
                <option value="CA">Canada</option>
              </select>
            </div>
          </div>

          {/* Same as Billing Checkbox */}
          <div style={{
            marginBottom: '1.5rem',
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#f9f9f9',
            borderRadius: '8px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}>
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => setSameAsBilling(e.target.checked)}
                style={{ marginRight: '0.75rem', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500' }}>Shipping address same as billing</span>
            </label>
          </div>

          {/* Shipping Address Section */}
          {!sameAsBilling && (
            <>
              <h3 style={{
                marginBottom: '1rem', 
                color: '#CDB688', 
                fontSize: window.innerWidth < 400 ? '1rem' : '1.1rem', 
                marginTop: '1.5rem'
              }}>
                <i className="fas fa-shipping-fast"></i> Shipping Address
              </h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#333',
                  fontWeight: '500',
                  fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                }}>
                  Address Line 1 * <span style={{color: '#999', fontSize: window.innerWidth > 600 ? '0.8rem' : '0.75rem', fontWeight: 'normal', display: window.innerWidth < 400 ? 'block' : 'inline', marginTop: window.innerWidth < 400 ? '0.25rem' : '0'}}>(Enter your full address)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="input"
                    value={shippingAddress}
                    onChange={(e) => handleAddressInputChange(e.target.value, 'shipping')}
                    onFocus={() => { if (shippingAddress.length >= 3) searchAddress(shippingAddress, 'shipping'); }}
                    placeholder="Start typing your address..."
                    required={!sameAsBilling}
                    autoComplete="off"
                    style={{ fontSize: '16px' }}
                  />
                  {showSuggestions && activeSuggestionType === 'shipping' && addressSuggestions.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}>
                      {addressSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => selectAddress(suggestion)}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderBottom: index < addressSuggestions.length - 1 ? '1px solid #eee' : 'none',
                            fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          {suggestion.properties.formatted}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#333',
                  fontWeight: '500',
                  fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                }}>
                  Address Line 2
                </label>
                <input
                  type="text"
                  className="input"
                  value={shippingAddress2}
                  onChange={(e) => setShippingAddress2(e.target.value)}
                  placeholder="Apartment, suite, etc."
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    City *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder="City"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    State/Province *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingState}
                    onChange={(e) => setShippingState(e.target.value)}
                    placeholder="State"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    ZIP/Postal Code *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder="Postal code"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    Country *
                  </label>
                  <select
                    className="input"
                    value={shippingCountry}
                    onChange={(e) => setShippingCountry(e.target.value)}
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  >
                    <option value="ZA">South Africa</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="AU">Australia</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth < 400 ? 'column' : 'row',
            gap: '1rem', 
            marginTop: '1.5rem' 
          }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowAddressModal(false)}
              style={{ 
                flex: 1,
                minHeight: '44px',
                fontSize: window.innerWidth < 400 ? '0.95rem' : '1rem'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              style={{ 
                flex: 1,
                minHeight: '44px',
                fontSize: window.innerWidth < 400 ? '0.95rem' : '1rem'
              }}
            >
              Save Address
            </button>
          </div>
        </form>
      </Modal>

      <BottomNav />
    </div>
  );
};

export default Profile;
